import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { Match, Campaign } from '../../types';
import { MatchCard } from './MatchCard';
import { Spinner } from '../common/Spinner';
import toast from 'react-hot-toast';

interface MatchesPageProps {}

export const MatchesPage: React.FC<MatchesPageProps> = () => {
  const { t } = useTranslation('matches');
  const { user } = useAuthStore();
  const [matches, setMatches] = useState<Match[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'applied' | 'shortlisted' | 'requested' | 'accepted' | 'rejected'>('all');

  useEffect(() => {
    console.log('🔍 [MatchesPage] useEffect triggered');
    console.log('🔍 [MatchesPage] User object:', user);
    console.log('🔍 [MatchesPage] User type:', user?.userType);
    console.log('🔍 [MatchesPage] User ID:', user?.userId);
    console.log('🔍 [MatchesPage] Filter:', filter);

    fetchMatches();
    if (user?.userType === 'host') {
      fetchHostCampaigns();
    }
  }, [user, filter]);

  const fetchMatches = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let response;

      if (user.userType === 'influencer') {
        // Get influencer's applications
        response = await graphqlApi.getMatches({
          userId: user.userId,
          status: filter !== 'all' ? filter : undefined
        });
      } else if (user.userType === 'host') {
        // Get applications to host's campaigns
        try {
          const hostProfile = await graphqlApi.getHostProfileByUserId(user.userId);
          console.log('🔍 Debug - Host profile response:', hostProfile);
          console.log('🔍 Debug - Host profile data:', hostProfile.data);
          console.log('🔍 Debug - Host ID from profile:', hostProfile.data?.hostId);

          if (hostProfile.success && hostProfile.data?.hostId) {
            console.log('🚀 Debug - Fetching applications for hostId:', hostProfile.data.hostId);
            response = await graphqlApi.getHostApplications(hostProfile.data.hostId);
            console.log('🔍 Debug - Applications response:', response);
          } else {
            // Host profile doesn't exist yet, return empty data
            setMatches([]);
            return;
          }
        } catch (error) {
          console.error('Error fetching host profile:', error);
          // Gracefully handle the error by showing empty state
          setMatches([]);
          return;
        }
      }

      if (response?.success && response.data?.matches) {
        setMatches(response.data.matches || []);
      } else if (response?.success && !response.data) {
        // Backend returned success but no data - show empty state
        setMatches([]);
      } else {
        setMatches([]);
      }
    } catch (error: any) {
      console.error('Error fetching matches:', error);
      toast.error(t('toast.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchHostCampaigns = async () => {
    if (!user || user.userType !== 'host') return;

    try {
      const hostProfile = await graphqlApi.getHostProfileByUserId(user.userId);
      if (hostProfile.success && hostProfile.data?.hostId) {
        const response = await graphqlApi.getCampaigns({
          hostId: hostProfile.data.hostId
        });

        if (response.success && response.data) {
          setCampaigns(response.data.campaigns || []);
        }
      }
    } catch (error: any) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleMatchUpdate = async (matchId: string, action: 'approve' | 'reject', feedback?: string) => {
    try {
      let response;
      if (action === 'approve') {
        response = await graphqlApi.approveMatch(matchId, feedback);
      } else {
        response = await graphqlApi.rejectMatch(matchId, feedback || 'Application rejected');
      }

      if (response.success) {
        toast.success(t(`toast.${action}Success`));
        await fetchMatches(); // Refresh the list
      } else {
        toast.error(t(`toast.${action}Error`));
      }
    } catch (error: any) {
      console.error(`Error ${action}ing match:`, error);
      toast.error(t(`toast.${action}Error`));
    }
  };

  const getFilteredMatches = () => {
    if (filter === 'all') return matches;
    return matches.filter(match => match.status === filter);
  };

  const getStats = () => {
    const totalApplications = matches.length;
    const appliedApplications = matches.filter(m => m.status === 'applied').length;
    const shortlistedApplications = matches.filter(m => m.status === 'shortlisted').length;
    const requestedApplications = matches.filter(m => m.status === 'requested').length;
    const acceptedApplications = matches.filter(m => m.status === 'accepted').length;
    const rejectedApplications = matches.filter(m => m.status === 'rejected').length;

    return { totalApplications, appliedApplications, shortlistedApplications, requestedApplications, acceptedApplications, rejectedApplications };
  };

  const stats = getStats();
  const filteredMatches = getFilteredMatches();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {user?.userType === 'host' ? t('title.host') : t('title.influencer')}
        </h1>
        <p className="mt-2 text-gray-600">
          {user?.userType === 'host' ? t('subtitle.host') : t('subtitle.influencer')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.totalApplications')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.pending')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.requestedApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.approved')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.acceptedApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.rejected')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.rejectedApplications}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex space-x-4">
          {(['all', 'applied', 'shortlisted', 'requested', 'accepted', 'rejected'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                filter === status
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {t(`filters.${status}`)}
              <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {status === 'all' ? matches.length : matches.filter(m => m.status === status).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      ) : filteredMatches.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {filter === 'all' ? t('empty.noApplications') : t('empty.noFilteredApplications', { filter: t(`filters.${filter}`) })}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {user?.userType === 'host' ? t('empty.hostMessage') : t('empty.influencerMessage')}
          </p>
          {user?.userType === 'influencer' && (
            <div className="mt-6">
              <a
                href="/discover"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {t('empty.discoverCampaigns')}
              </a>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {filteredMatches.map((match) => (
            <MatchCard
              key={match.matchId}
              match={match}
              onUpdate={handleMatchUpdate}
              userType={user?.userType}
            />
          ))}
        </div>
      )}
    </div>
  );
};
